/**
 * Générateur — Utiliser une multiplication pour appliquer une fraction à un nombre entier
 * Attendu ID : 626 | Niveau : 6ème | Domaine : NC (Nombres et calculs)
 *
 * Ce générateur contient 8 EXERCICES DIDACTIQUEMENT DISTINCTS (chacun avec
 * son propre generatorKey au niveau exercice), déclinés en plusieurs formats UI :
 *
 *   1. `fraction_entier_direct`            → QCM unique + Saisie
 *      Calculer « b/c de a » avec c | a, résultat entier. Sous-famille A.
 *
 *   2. `fraction_entier_fraction_resultat` → QCM unique + Saisie
 *      Calculer « b/c × a » avec c ∤ a, résultat fractionnaire irréductible. Sous-famille B.
 *
 *   3. `fraction_heure_minutes`            → QCM unique + Saisie
 *      Convertir une fraction propre d'heure en minutes. Sous-famille C.
 *
 *   4. `fraction_heure_impropre_hm`        → Saisie uniquement
 *      Transformer une fraction d'heure impropre en « h + min ». Sous-famille C₂.
 *
 *   5. `fraction_longueur_segment`         → QCM unique + Saisie (+ SVG inline)
 *      Calculer AC = b/c × L sur un segment [AB] avec support visuel SVG. Sous-famille D₁.
 *
 *   6. `fraction_diagramme`                → Saisie uniquement (+ SVG inline)
 *      Calculer une part d'une grandeur totale lue sur un diagramme semi-circulaire. Sous-famille D₂.
 *
 *   7. `egalite_expressions_frac`          → V/F uniquement
 *      Juger l'égalité entre deux écritures fractionnaires d'un même calcul.
 *
 *   8. `multiplication_schema_bandes`      → QCM unique + Saisie (+ SVG inline)
 *      Effectuer n × b/c avec un schéma en bandes. Sous-famille A/B.
 *
 * Règles dures de la fiche cluster 14 (attendu 626) :
 *   - POOL A : c | a, a/c ≥ 2, résultat (b × a)/c ∈ [2, 500]
 *   - POOL B : c ∤ a, b × a ≤ 200, résultat sous forme irréductible
 *   - POOL C : c ∈ {2, 3, 4, 5, 6, 10, 12, 15, 20, 30} qui divise 60, b < c
 *   - POOL C₂ : b/c > 1, (b × 60)/c ∈ ℕ, total ≤ 600 min
 *   - POOL D₁ : c | L, b < c, AC ∈ [5, L − 5]
 *   - POOL D₂ : c | G pour chaque part lue sur le diagramme, somme des parts ≤ 1
 *   - POOL H : n ∈ [2, 6], (b, c) irréductibles avec b < c ou b = c + k léger
 *
 * Les 7 familles d'erreur contextualisées (distracteurs) :
 *   1. Oubli du dénominateur : `b × a` au lieu de `(b × a) / c`
 *   2. Oubli du numérateur : `a / c` au lieu de `(b × a) / c`
 *   3. Inversion b ↔ c : `(c × a) / b`
 *   4. Addition/soustraction au lieu de multiplication (« de » → « plus/moins »)
 *   5. Division entière bloquée (tronque une fraction qui doit rester fractionnaire)
 *   6. Complément géométrique (donne CB au lieu de AC)
 *   7. Confusion « et quart » / « moins un quart » (oubli du +60 min)
 */

import {
    randomChoice, shuffle,
    makeQcm, makeSaisie, makeVraiFaux, makeTexteTrou, tex,
} from '../../../utils';
import { gcd, fmtFrac, fmtKatex, fmtItem, fmtItemBrut } from './fraction';

// Fractions en KaTeX partout où l'élève les lit (audit fractions en texte,
// 2026-09-06). Dans un énoncé, la fraction s'affiche TELLE QU'ÉCRITE (sans
// simplifier : fmtItemBrut, pas fmtItem) ; « b/c × a » devient une formule.
const fracBrut = (num, den) => fmtItemBrut(num, den);
const produitTex = (b, c, a) => tex(`\\dfrac{${b}}{${c}} \\times ${a}`);
/** Ligne du pool V/F « … 2/3 × 15 » ou « … 6 × 48/24 » → produit en KaTeX. */
const ligneVfTex = (ligne) => ligne
    .replace(/(\d+)\/(\d+) × (\d+)/g, (m, a, b, n) => tex(`\\dfrac{${a}}{${b}} \\times ${n}`))
    .replace(/(\d+) × (\d+)\/(\d+)/g, (m, n, a, b) => tex(`${n} \\times \\dfrac{${a}}{${b}}`));

// ═══════════════════════════════════════════════════════════
// CONSTANTES & LABELS
// ═══════════════════════════════════════════════════════════

const ATTENDU_ID = 626;
const opts = (label) => ({ niveau: '6eme', matiere: 'maths', attendu: ATTENDU_ID, label });

const L_ENT_QCM    = 'Fraction d\'un entier, résultat entier (QCM)';
const L_ENT_SAI    = 'Fraction d\'un entier, résultat entier (Saisie)';
const L_FRAC_QCM   = 'Fraction d\'un entier, résultat fractionnaire (QCM)';
const L_FRAC_SAI   = 'Fraction d\'un entier, résultat fractionnaire (Saisie)';
const L_HMIN_QCM   = 'Fraction d\'heure en minutes (QCM)';
const L_HMIN_SAI   = 'Fraction d\'heure en minutes (Saisie)';
const L_HIMP_SAI   = 'Fraction d\'heure impropre en h + min (Saisie)';
const L_SEG_QCM    = 'Fraction d\'une longueur sur un segment (QCM)';
const L_SEG_SAI    = 'Fraction d\'une longueur sur un segment (Saisie)';
const L_DIAG_SAI   = 'Fraction d\'une grandeur totale sur un diagramme (Saisie)';
const L_VF         = 'Égalité entre expressions fractionnaires (Vrai/Faux)';
const L_BANDES_QCM = 'Multiplication entier × fraction avec schéma en bandes (QCM)';
const L_BANDES_SAI = 'Multiplication entier × fraction avec schéma en bandes (Saisie)';

// ═══════════════════════════════════════════════════════════
// HELPERS ARITHMÉTIQUES
// ═══════════════════════════════════════════════════════════

/** Réduit une fraction à sa forme irréductible (dénominateur positif). */
const reduceFrac = (num, den) => {
    if (den === 0) throw new Error(`reduceFrac: den=0 (num=${num})`);
    const g = gcd(num, den);
    const sign = den < 0 ? -1 : 1;
    return { num: sign * num / g, den: Math.abs(den) / g };
};

/** Validation d'un triplet 626 : a, b, c entiers positifs, c ≥ 1. */
const isValid626 = (a, b, c) =>
    Number.isInteger(a) && Number.isInteger(b) && Number.isInteger(c)
    && a >= 1 && b >= 1 && c >= 1;

/** Nettoyage des distracteurs : unicité + complétion par fallback. */
const cleanDistracteurs = (bonne, dists, n, fallbackFn) => {
    const out = [];
    const seen = new Set([String(bonne)]);
    for (const d of dists) {
        const s = String(d);
        if (!seen.has(s)) { out.push(d); seen.add(s); }
    }
    let guard = 0;
    while (out.length < n && guard < 40) {
        const cand = fallbackFn(guard);
        const s = String(cand);
        if (s && !seen.has(s)) { out.push(cand); seen.add(s); }
        guard++;
    }
    return out.slice(0, n);
};

// ═══════════════════════════════════════════════════════════
// POOLS DE VALEURS — tous vérifiés par isValid626
// ═══════════════════════════════════════════════════════════

/**
 * POOL_A — Résultat entier, sous-famille A.
 * Triplets [a, b, c] avec c | a, a/c ≥ 2, résultat (b×a)/c ∈ [2, 500].
 * Mélange fractions propres (b<c) et impropres (b>c) qui donnent quand même un entier.
 */
const POOL_A_RAW = [
    [60,  2, 5],  // 24
    [60,  3, 5],  // 36
    [60,  4, 5],  // 48
    [60,  5, 6],  // 50
    [80,  3, 4],  // 60
    [80,  5, 8],  // 50
    [40,  3, 5],  // 24
    [40,  3, 4],  // 30
    [24,  5, 6],  // 20
    [24,  7, 6],  // 28 (impropre)
    [24,  3, 4],  // 18
    [24,  5, 4],  // 30 (impropre)
    [30,  2, 3],  // 20
    [30,  5, 6],  // 25
    [30,  4, 5],  // 24
    [30,  7, 6],  // 35 (impropre)
    [36,  5, 6],  // 30
    [36,  7, 12], // 21
    [36,  5, 4],  // 45 (impropre)
    [48,  7, 12], // 28
    [48,  5, 6],  // 40
    [48,  3, 4],  // 36
    [48,  5, 8],  // 30
    [48,  7, 8],  // 42
    [72,  5, 6],  // 60
    [72,  5, 12], // 30
    [72,  7, 12], // 42
    [100, 3, 10], // 30
    [100, 7, 10], // 70
    [100, 3, 5],  // 60
    [120, 5, 6],  // 100
    [120, 7, 12], // 70
    [120, 7, 10], // 84
    [140, 3, 7],  // 60
    [140, 3, 4],  // 105
    [140, 3, 5],  // 84
    [150, 2, 5],  // 60
    [150, 7, 10], // 105
    [150, 4, 5],  // 120
    [200, 3, 4],  // 150
    [200, 3, 5],  // 120
    [200, 7, 10], // 140
    [240, 5, 8],  // 150
    [240, 7, 12], // 140
];

/**
 * POOL_B — Résultat fractionnaire, sous-famille B.
 * Triplets [a, b, c] avec c ∤ a, b×a ≤ 200, résultat (b×a)/c non entier ET
 * idéalement irréductible (sinon la simplification sera obligatoire côté correction).
 */
const POOL_B_RAW = [
    [3,  5, 4],  // 15/4
    [3,  7, 4],  // 21/4
    [5,  3, 4],  // 15/4
    [5,  7, 4],  // 35/4
    [7,  2, 3],  // 14/3
    [7,  4, 3],  // 28/3
    [5,  2, 3],  // 10/3
    [5,  4, 3],  // 20/3
    [8,  3, 5],  // 24/5
    [9,  2, 5],  // 18/5
    [7,  4, 5],  // 28/5
    [11, 3, 4],  // 33/4
    [13, 2, 3],  // 26/3
    [9,  5, 4],  // 45/4
    [11, 2, 5],  // 22/5
    [13, 3, 5],  // 39/5
    [9,  4, 7],  // 36/7
    [15, 2, 7],  // 30/7
    [8,  5, 7],  // 40/7
    [11, 4, 5],  // 44/5
    [6,  5, 4],  // 30/4 — réductible (15/2) : sera filtré par isIrreductible
    [7,  3, 5],  // 21/5
    [11, 5, 3],  // 55/3
    [5,  3, 7],  // 15/7
    [9,  2, 7],  // 18/7
    [13, 4, 5],  // 52/5
];

/**
 * POOL_C — Durées propres (fractions d'heure).
 * [b, c] avec c ∈ {2,3,4,5,6,10,12,15,20,30} (diviseurs de 60) et b < c.
 */
const POOL_C_RAW = [
    [1, 2],  [1, 3],  [2, 3],  [1, 4],  [3, 4],
    [1, 5],  [2, 5],  [3, 5],  [4, 5],
    [1, 6],  [5, 6],
    [1, 10], [3, 10], [7, 10], [9, 10],
    [1, 12], [5, 12], [7, 12], [11, 12],
    [1, 15],
    [1, 20],
    [1, 30],
];

/**
 * POOL_C2 — Durées impropres (b/c > 1).
 * [b, c] avec b > c, (b × 60) / c ∈ ℕ, total ≤ 600 min (≤ 10 h).
 */
const POOL_C2_RAW = [
    [7,  4], // 105 → 1h45
    [11, 4], // 165 → 2h45
    [13, 4], // 195 → 3h15
    [15, 4], // 225 → 3h45
    [9,  2], // 270 → 4h30
    [11, 2], // 330 → 5h30
    [13, 2], // 390 → 6h30
    [5,  3], // 100 → 1h40
    [7,  3], // 140 → 2h20
    [8,  3], // 160 → 2h40
    [11, 3], // 220 → 3h40
    [13, 3], // 260 → 4h20
    [6,  5], // 72  → 1h12
    [8,  5], // 96  → 1h36
    [9,  5], // 108 → 1h48
    [12, 5], // 144 → 2h24
    [7,  6], // 70  → 1h10
    [11, 6], // 110 → 1h50
    [13, 6], // 130 → 2h10
];

/**
 * POOL_D1 — Longueur d'un segment.
 * [L, b, c] avec c | L, b < c, AC = (b × L)/c ∈ [5, L − 5].
 */
const POOL_D1_RAW = [
    [24, 1, 2],  // 12
    [24, 1, 3],  // 8
    [24, 2, 3],  // 16
    [24, 1, 4],  // 6
    [24, 3, 4],  // 18
    [24, 5, 6],  // 20
    [24, 3, 8],  // 9
    [30, 2, 3],  // 20
    [30, 3, 5],  // 18
    [30, 2, 5],  // 12
    [30, 4, 5],  // 24
    [30, 5, 6],  // 25
    [30, 7, 10], // 21
    [30, 3, 10], // 9
    [36, 2, 3],  // 24
    [36, 3, 4],  // 27
    [36, 5, 6],  // 30
    [36, 7, 12], // 21
    [42, 2, 3],  // 28
    [42, 3, 7],  // 18
    [42, 4, 7],  // 24
    [42, 5, 7],  // 30
    [42, 5, 6],  // 35
    [48, 3, 4],  // 36
    [48, 3, 8],  // 18
    [48, 5, 8],  // 30
    [48, 5, 6],  // 40
    [48, 7, 12], // 28
    [56, 3, 7],  // 24
    [56, 5, 7],  // 40
    [56, 5, 8],  // 35
    [63, 5, 7],  // 45
    [63, 2, 7],  // 18
    [63, 4, 7],  // 36
    [63, 2, 3],  // 42
    [72, 5, 6],  // 60
    [72, 3, 4],  // 54
    [72, 7, 12], // 42
    [72, 5, 12], // 30
    [72, 5, 8],  // 45
];

/**
 * POOL_D2 — Diagrammes préconçus. Chaque entrée est un scénario complet avec :
 *   G (grandeur totale), parts (liste de {label, num, den, fillRGB, labelRGB}),
 *   unite_pl (pluriel), unite_sg (singulier), contexte (prénom, décor).
 * Les parts somment à 1 (partition totale), et chaque (num/den) vérifie c | G.
 */
const POOL_D2 = [
    {
        G: 140,
        contexte: 'M. Eugène a représenté la répartition des cultures sur ses 140 hectares',
        unite_pl: 'hectares', unite_sg: 'hectare',
        parts: [
            { label: 'Blé',  num: 1, den: 2,  fill: '#bfdbfe', color: '#1e3a8a' }, // 70
            { label: 'Maïs', num: 2, den: 5,  fill: '#fde68a', color: '#78350f' }, // 56
            { label: 'Orge', num: 1, den: 10, fill: '#d1fae5', color: '#065f46' }, // 14
        ],
    },
    {
        G: 120,
        contexte: 'Une classe de 120 élèves est répartie en clubs selon le diagramme suivant',
        unite_pl: 'élèves', unite_sg: 'élève',
        parts: [
            { label: 'Théâtre', num: 1, den: 3, fill: '#fecaca', color: '#7f1d1d' }, // 40
            { label: 'Sport',   num: 1, den: 2, fill: '#bae6fd', color: '#0c4a6e' }, // 60
            { label: 'Musique', num: 1, den: 6, fill: '#ddd6fe', color: '#4c1d95' }, // 20
        ],
    },
    {
        G: 200,
        contexte: 'Une librairie a vendu 200 livres cette semaine, répartis selon le diagramme suivant',
        unite_pl: 'livres', unite_sg: 'livre',
        parts: [
            { label: 'Romans',  num: 2, den: 5,  fill: '#fecaca', color: '#7f1d1d' }, // 80
            { label: 'BD',      num: 3, den: 10, fill: '#fde68a', color: '#78350f' }, // 60
            { label: 'Manuels', num: 1, den: 4,  fill: '#bae6fd', color: '#0c4a6e' }, // 50
            { label: 'Autres',  num: 1, den: 20, fill: '#d1fae5', color: '#065f46' }, // 10
        ],
    },
    {
        G: 180,
        contexte: 'Un magasin de fruits a vendu 180 kilogrammes de fruits dans la journée',
        unite_pl: 'kilogrammes', unite_sg: 'kilogramme',
        parts: [
            { label: 'Pommes',  num: 1, den: 2, fill: '#fde68a', color: '#78350f' }, // 90
            { label: 'Oranges', num: 1, den: 3, fill: '#fed7aa', color: '#7c2d12' }, // 60
            { label: 'Poires',  num: 1, den: 6, fill: '#bbf7d0', color: '#14532d' }, // 30
        ],
    },
    {
        G: 240,
        contexte: 'Un éleveur compte 240 animaux dans sa ferme',
        unite_pl: 'animaux', unite_sg: 'animal',
        parts: [
            { label: 'Vaches',   num: 1, den: 4, fill: '#fecaca', color: '#7f1d1d' }, // 60
            { label: 'Moutons',  num: 1, den: 3, fill: '#e5e7eb', color: '#1f2937' }, // 80
            { label: 'Poules',   num: 1, den: 2, fill: '#fef3c7', color: '#78350f' }, // 120
            // Note : somme = 1/4 + 1/3 + 1/2 = 3/12 + 4/12 + 6/12 = 13/12 > 1, donc je corrige :
            // Remplaçons par 1/6 de poules (40) → somme 1/4 + 1/3 + 1/6 = 3/12 + 4/12 + 2/12 = 9/12
            // Il reste 3/12 = 1/4 « lapins » ?
        ],
    },
    {
        G: 160,
        contexte: 'Un boulanger a préparé 160 viennoiseries pour la journée',
        unite_pl: 'viennoiseries', unite_sg: 'viennoiserie',
        parts: [
            { label: 'Croissants',    num: 3, den: 8, fill: '#fde68a', color: '#78350f' }, // 60
            { label: 'Pains au choc', num: 1, den: 4, fill: '#fed7aa', color: '#7c2d12' }, // 40
            { label: 'Brioches',      num: 1, den: 5, fill: '#fef3c7', color: '#78350f' }, // 32
            { label: 'Chouquettes',   num: 1, den: 8, fill: '#dbeafe', color: '#1e3a8a' }, // 20
            // somme : 3/8 + 1/4 + 1/5 + 1/8 = 15/40 + 10/40 + 8/40 + 5/40 = 38/40 — on met "autres" 2/40 = 1/20 ?
            // c=20 | 160 ✓ → "Autres" num=1, den=20 → 8
        ],
    },
];

/** On corrige POOL_D2[4] pour que la somme soit ≤ 1 : on retire l'entrée Poules invalide. */
POOL_D2[4] = {
    G: 240,
    contexte: 'Un éleveur compte 240 animaux dans sa ferme',
    unite_pl: 'animaux', unite_sg: 'animal',
    parts: [
        { label: 'Vaches',  num: 1, den: 4, fill: '#fecaca', color: '#7f1d1d' }, // 60
        { label: 'Moutons', num: 1, den: 3, fill: '#e5e7eb', color: '#1f2937' }, // 80
        { label: 'Poules',  num: 1, den: 6, fill: '#fef3c7', color: '#78350f' }, // 40
        { label: 'Lapins',  num: 1, den: 4, fill: '#ddd6fe', color: '#4c1d95' }, // 60
        // somme : 1/4 + 1/3 + 1/6 + 1/4 = 3/12 + 4/12 + 2/12 + 3/12 = 12/12 = 1 ✓
    ],
};

/** Idem POOL_D2[5] : on ajoute "Autres" pour fermer la somme à 1. */
POOL_D2[5] = {
    G: 160,
    contexte: 'Un boulanger a préparé 160 viennoiseries pour la journée',
    unite_pl: 'viennoiseries', unite_sg: 'viennoiserie',
    parts: [
        { label: 'Croissants',    num: 3, den: 8,  fill: '#fde68a', color: '#78350f' }, // 60
        { label: 'Pains au choc', num: 1, den: 4,  fill: '#fed7aa', color: '#7c2d12' }, // 40
        { label: 'Brioches',      num: 1, den: 5,  fill: '#fef3c7', color: '#78350f' }, // 32
        { label: 'Chouquettes',   num: 1, den: 8,  fill: '#dbeafe', color: '#1e3a8a' }, // 20
        { label: 'Autres',        num: 1, den: 20, fill: '#d1fae5', color: '#065f46' }, // 8
        // somme : 3/8 + 1/4 + 1/5 + 1/8 + 1/20 = 15/40 + 10/40 + 8/40 + 5/40 + 2/40 = 40/40 = 1 ✓
    ],
};

/**
 * POOL_H — Schéma en bandes.
 * [n, b, c] avec n ∈ [2, 6], (b, c) irréductibles avec 1 ≤ b ≤ c + 1 et 2 ≤ c ≤ 6.
 */
const POOL_H_RAW = [
    [2, 1, 3], [2, 2, 3], [2, 1, 4], [2, 3, 4], [2, 1, 5], [2, 2, 5], [2, 3, 5], [2, 4, 5],
    [3, 1, 2], [3, 1, 3], [3, 2, 3], [3, 1, 4], [3, 3, 4], [3, 1, 5], [3, 2, 5], [3, 4, 5],
    [4, 1, 2], [4, 1, 3], [4, 2, 3], [4, 1, 4], [4, 3, 4], [4, 1, 5], [4, 2, 5], [4, 3, 5],
    [5, 1, 2], [5, 1, 3], [5, 2, 3], [5, 1, 4], [5, 3, 4], [5, 1, 5], [5, 2, 5], [5, 4, 5],
    [6, 1, 2], [6, 1, 3], [6, 2, 3], [6, 1, 4], [6, 3, 4], [6, 1, 5], [6, 2, 5],
];

// ─── Filtrage préventif : on ne garde que les triplets valides ────────────

const POOL_A = POOL_A_RAW.filter(([a, b, c]) =>
    isValid626(a, b, c) && a % c === 0 && (a / c) >= 2
    && (b * a / c) >= 2 && (b * a / c) <= 500);

const POOL_B = POOL_B_RAW.filter(([a, b, c]) => {
    if (!isValid626(a, b, c)) return false;
    if (a % c === 0) return false;          // résultat entier : refuser
    if (b * a > 200) return false;
    return true;
});

/** Sous-ensemble de POOL_B où (b×a)/c est DÉJÀ irréductible (pour pédagogie plus lisible). */
const POOL_B_IRRED = POOL_B.filter(([a, b, c]) => gcd(b * a, c) === 1);

const POOL_C = POOL_C_RAW.filter(([b, c]) =>
    c > 0 && b > 0 && b < c && 60 % c === 0);

const POOL_C2 = POOL_C2_RAW.filter(([b, c]) => {
    if (c <= 0 || b <= 0 || b <= c) return false;
    const total = (b * 60) / c;
    return Number.isInteger(total) && total <= 600;
});

const POOL_D1 = POOL_D1_RAW.filter(([L, b, c]) => {
    if (!isValid626(L, b, c)) return false;
    if (L % c !== 0) return false;
    if (b >= c) return false;
    const AC = (b * L) / c;
    return AC >= 5 && AC <= L - 5 && Number.isInteger(AC);
});

const POOL_H = POOL_H_RAW.filter(([n, b, c]) =>
    Number.isInteger(n) && Number.isInteger(b) && Number.isInteger(c)
    && n >= 2 && n <= 6 && b >= 1 && c >= 2 && c <= 6
    && gcd(b, c) === 1);

// ═══════════════════════════════════════════════════════════
// PRÉNOMS & CONTEXTES
// ═══════════════════════════════════════════════════════════

// ═══════════════════════════════════════════════════════════
// HELPERS GRAMMAIRE FRANÇAISE
// ═══════════════════════════════════════════════════════════

/** Élision "de" → "d'" devant voyelle/h muet. */
const dePrep = (word) => {
    const first = word.normalize('NFD').replace(/[\u0300-\u036f]/g, '')[0].toLowerCase();
    return /[aeiouh]/.test(first) ? "d'" : 'de ';
};

// ═══════════════════════════════════════════════════════════
// HELPERS SVG (data URL pour compatibilité DOMPurify)
// ═══════════════════════════════════════════════════════════

// Couleurs claires pour le dark-mode (bg-slate-800/50 dans ImageElementEleve)
const SVG_STROKE = 'currentColor';
const SVG_TEXT = 'currentColor';
const SVG_TEXT_SEC = '#94a3b8';

/** Helper : construit les elements_additionnels avec une image SVG data URL. */
const svgImageElement = (dataUrl, maxWidth = 'compact') => ({
    version: 1,
    elements: [{ type: 'image', config: { imageUrl: dataUrl, maxWidth } }],
});

/**
 * SVG d'un segment horizontal [AB] étiqueté — retourne un data URL.
 * @param {number} Lmm — longueur affichée en mm (texte sous le segment)
 */
const svgSegment = (Lmm) => {
    const svg = `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 500 60" width="500" height="60" role="img" aria-label="Segment [AB] de ${Lmm} mm">`
        + `<line x1="30" y1="30" x2="470" y2="30" stroke="${SVG_STROKE}" stroke-width="2"/>`
        + `<line x1="30" y1="20" x2="30" y2="40" stroke="${SVG_STROKE}" stroke-width="2"/>`
        + `<line x1="470" y1="20" x2="470" y2="40" stroke="${SVG_STROKE}" stroke-width="2"/>`
        + `<text x="30" y="14" text-anchor="middle" font-size="14" font-weight="bold" fill="${SVG_TEXT}">A</text>`
        + `<text x="470" y="14" text-anchor="middle" font-size="14" font-weight="bold" fill="${SVG_TEXT}">B</text>`
        + `<text x="250" y="54" text-anchor="middle" font-size="12" fill="${SVG_TEXT_SEC}">${Lmm} mm</text>`
        + `</svg>`;
    return `data:image/svg+xml,${encodeURIComponent(svg)}`;
};

/**
 * SVG d'un schéma en bandes : n bandes rectangulaires, chacune divisée en c parts égales,
 * les b premières parts coloriées par bande, les (c − b) dernières laissées blanches.
 * Cette fonction génère MÉCANIQUEMENT les n × c rectangles (corrige la borne du payload exemple).
 * @param {number} n  — nombre de bandes
 * @param {number} b  — parts coloriées par bande
 * @param {number} c  — parts totales par bande
 */
const svgBandes = (n, b, c) => {
    // Dimensions : chaque part = 24 px de large, espace entre bandes = 14 px.
    const partW = 24;
    const bandW = partW * c;
    const gap = 14;
    const marginX = 20;
    const width = marginX * 2 + bandW * n + gap * (n - 1);
    const height = 80;
    const y = 20;
    const partH = 40;
    const fillColor = '#3b82f6';   // bleu visible sur fond sombre
    const emptyColor = 'rgba(255,255,255,0.1)';

    // Remplissage À LA SUITE (revue 2026-08-02, demande user) : les n × b
    // parts coloriées sont consécutives — la première bande se complète
    // avant d'entamer la suivante, l'élève VOIT le total (entiers + reste).
    const totalRempli = n * b;
    let rects = '';
    for (let i = 0; i < n; i++) {
        const bandX = marginX + i * (bandW + gap);
        for (let j = 0; j < c; j++) {
            const x = bandX + j * partW;
            const color = (i * c + j) < totalRempli ? fillColor : emptyColor;
            rects += `<rect x="${x}" y="${y}" width="${partW}" height="${partH}" fill="${color}"/>`;
        }
    }
    const svg = `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 ${width} ${height}" width="${width}" height="${height}" role="img" aria-label="Schéma ${n} bandes divisées en ${c} parts, ${totalRempli} parts coloriées à la suite">`
        + `<g stroke="${SVG_STROKE}" stroke-width="1.5">${rects}</g>`
        + `</svg>`;
    return `data:image/svg+xml,${encodeURIComponent(svg)}`;
};

/**
 * SVG d'un diagramme semi-circulaire réparti en secteurs.
 * Chaque part reçoit un angle proportionnel à sa fraction (180° × num/den).
 * @param {Object} scenario — entrée de POOL_D2 : { parts: [{label, num, den, fill, color}] }
 */
const svgDiagramme = (scenario) => {
    const cx = 150, cy = 170, r = 130;
    // Secteur de 180° (demi-cercle supérieur) : angle 180° en haut à gauche, 0° en haut à droite.
    // Paramétrage : on part de l'angle 180° (gauche) et on "avance" dans le sens horaire.
    let cumul = 0; // en fraction de 1
    const paths = [];
    const labels = [];

    for (const part of scenario.parts) {
        const pFrac = part.num / part.den;
        const startFrac = cumul;
        const endFrac = cumul + pFrac;
        const thetaStart = Math.PI - startFrac * Math.PI;
        const thetaEnd = Math.PI - endFrac * Math.PI;
        const x1 = cx + r * Math.cos(thetaStart);
        const y1 = cy - r * Math.sin(thetaStart);
        const x2 = cx + r * Math.cos(thetaEnd);
        const y2 = cy - r * Math.sin(thetaEnd);
        const largeArc = pFrac > 0.5 ? 1 : 0;
        paths.push(
            `<path d="M ${cx} ${cy} L ${x1.toFixed(2)} ${y1.toFixed(2)} A ${r} ${r} 0 ${largeArc} 1 ${x2.toFixed(2)} ${y2.toFixed(2)} Z" fill="${part.fill}" stroke="${SVG_STROKE}" stroke-width="1.5"/>`
        );
        cumul = endFrac;
    }

    // Légende à DROITE du diagramme (revue 2026-08-02) : les étiquettes
    // écrites dans les secteurs débordaient dès qu'une part était fine
    // (1/20 = 9 degrés d'angle).
    scenario.parts.forEach((part, i) => {
        const ly = 44 + i * 30;
        const fracTxt = `${part.num}/${part.den}`;
        labels.push(`<rect x="310" y="${ly - 11}" width="14" height="14" fill="${part.fill}" stroke="${SVG_STROKE}" stroke-width="1"/>`);
        labels.push(`<text x="332" y="${ly}" font-size="12" fill="${SVG_TEXT}">${part.label} : ${fracTxt}</text>`);
    });

    const svg = `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 480 200" width="480" height="200" role="img" aria-label="Diagramme semi-circulaire de répartition avec légende">`
        + paths.join('') + labels.join('')
        + `</svg>`;
    return `data:image/svg+xml,${encodeURIComponent(svg)}`;
};

// ═══════════════════════════════════════════════════════════
// EXERCICE 1 — fraction_entier_direct (QCM + Saisie)
// ═══════════════════════════════════════════════════════════

const consigneDirect = (a, b, c) => {
    const variations = [
        `<p>Calcule <strong>${fracBrut(b, c)}</strong> de <strong>${a}</strong>.</p>`,
        `<p>Que vaut <strong>${produitTex(b, c, a)}</strong> ?</p>`,
        `<p>Combien font les <strong>${fracBrut(b, c)}</strong> de <strong>${a}</strong> ?</p>`,
    ];
    return randomChoice(variations);
};

const genFractionEntierDirectQcm = () => {
    const [a, b, c] = randomChoice(POOL_A);
    const bonne = (b * a) / c;
    // Distracteurs fiche §4 : oubli c, oubli b, inversion, addition, soustraction.
    const cand = [
        b * a,                           // D1 : oubli c
        a / c,                           // D2 : oubli b
        (b !== 0 && (c * a) % b === 0) ? (c * a) / b : null, // D3 : inversion b↔c si entier
        a - b,                           // D4 : soustraction
        a + b,                           // D5 : addition
        b * a - c,                       // D6 : piège aléatoire
        Math.round(b * a / c) + 1,       // D7 : off-by-one
    ].filter(v => v !== null && v !== bonne && Number.isInteger(v) && v >= 0);

    const distracteurs = cleanDistracteurs(bonne, cand, 4, (i) => {
        if (i === 0) return bonne + 1;
        if (i === 1) return Math.max(0, bonne - 1);
        if (i === 2) return bonne * 2;
        return b + c;
    });

    const choix = [bonne, ...distracteurs].map(String);
    const payload = makeQcm(choix, String(bonne), consigneDirect(a, b, c), opts(L_ENT_QCM));
    payload._meta.generatorKey = 'fraction_entier_direct';
    return payload;
};

const genFractionEntierDirectSaisie = () => {
    const [a, b, c] = randomChoice(POOL_A);
    const bonne = (b * a) / c;
    const payload = makeSaisie(
        String(bonne),
        consigneDirect(a, b, c),
        opts(L_ENT_SAI),
    );
    payload._meta.generatorKey = 'fraction_entier_direct';
    return payload;
};

// ═══════════════════════════════════════════════════════════
// EXERCICE 2 — fraction_entier_fraction_resultat (QCM + Saisie)
// ═══════════════════════════════════════════════════════════

const consigneFracResultat = (a, b, c) => {
    const variations = [
        `<p>Calcule <strong>${produitTex(b, c, a)}</strong>. Donne ton résultat sous la forme d'une fraction la plus simple possible.</p>`,
        `<p>Que vaut <strong>${tex(`${b} \\times \\dfrac{${a}}{${c}}`)}</strong> ? Écris le résultat sous la forme d'une fraction.</p>`,
        `<p>Combien font les <strong>${fracBrut(b, c)}</strong> de <strong>${a}</strong> ? Écris le résultat sous la forme d'une fraction irréductible.</p>`,
    ];
    return randomChoice(variations);
};

const genFractionResultatQcm = () => {
    // On tire dans POOL_B_IRRED pour que le résultat irréductible soit directement (b×a)/c.
    const pool = POOL_B_IRRED.length > 0 ? POOL_B_IRRED : POOL_B;
    const [a, b, c] = randomChoice(pool);
    const numBrut = b * a;
    const { num, den } = reduceFrac(numBrut, c);
    const bonne = `${num}/${den}`;
    const bonneVal = num / den;
    const tryPush = (arr, s) => {
        const v = parseFracOrInt(s);
        if (Number.isFinite(v) && Math.abs(v - bonneVal) > 1e-9) arr.push(s);
    };

    // Distracteurs : résultat non simplifié si applicable, inversion, partie entière, addition.
    const cand = [];
    if (num !== numBrut || den !== c) tryPush(cand, `${numBrut}/${c}`);          // D1 : non simplifié
    tryPush(cand, `${a * c}/${b}`);                  // D2 : inversion b↔c
    tryPush(cand, String(Math.floor(numBrut / c)));  // D3 : partie entière (tronqué)
    tryPush(cand, `${a + b}/${c}`);                  // D4 : addition au numérateur
    tryPush(cand, String(b * a));                    // D5 : oubli total du /c
    tryPush(cand, `${Math.max(1, a - b)}/${c}`);     // D6 : soustraction au numérateur

    const distracteurs = cleanDistracteurs(bonne, cand, 4, (i) => {
        if (i === 0) return `${num + 1}/${den}`;
        if (i === 1) return `${num}/${den + 1}`;
        if (i === 2) return `${num - 1}/${den}`;
        return String(num + den);
    });

    const toKatex = (s) => {
        const m = s.match(/^(-?\d+)\/(\d+)$/);
        return m ? fmtItem(Number(m[1]), Number(m[2])) : s;
    };
    const bonneDisplay = toKatex(bonne);
    const choix = [bonne, ...distracteurs].map(toKatex);
    const payload = makeQcm(choix, bonneDisplay, consigneFracResultat(a, b, c), opts(L_FRAC_QCM));
    payload._meta.generatorKey = 'fraction_entier_fraction_resultat';
    return payload;
};

const genFractionResultatSaisie = () => {
    const pool = POOL_B_IRRED.length > 0 ? POOL_B_IRRED : POOL_B;
    const [a, b, c] = randomChoice(pool);
    const { num, den } = reduceFrac(b * a, c);
    // Saisie avec 2 zones (num/den) via texte_trou.
    // Étais \rule (revue 2026-08-02, même fix que 620/621/623/624) : sans
    // eux la barre de fraction disparaît sous les champs de saisie
    const phrase = `$\\text{Résultat : } \\dfrac{{{${num}}}\\rule[-0.55em]{0pt}{0.55em}}{\\rule{0pt}{1.3em}{{${den}}}}$`;
    const payload = makeTexteTrou(
        phrase,
        consigneFracResultat(a, b, c),
        { ...opts(L_FRAC_SAI), mode: 'saisie', strict: false },
    );
    payload._meta.generatorKey = 'fraction_entier_fraction_resultat';
    return payload;
};

// ═══════════════════════════════════════════════════════════
// EXERCICE 3 — fraction_heure_minutes (QCM + Saisie)
// ═══════════════════════════════════════════════════════════

/** Verbalisation d'une fraction propre d'heure. */
const verbHeurePropre = (b, c) => {
    // Cas spéciaux
    if (b === 1 && c === 2) return 'une demi-heure';
    if (b === 1 && c === 4) return 'un quart d\'heure';
    if (b === 3 && c === 4) return 'trois quarts d\'heure';
    if (b === 1 && c === 3) return 'un tiers d\'heure';
    if (b === 2 && c === 3) return 'deux tiers d\'heure';
    // Cas génériques
    const numWords = ['', 'un', 'deux', 'trois', 'quatre', 'cinq', 'six', 'sept', 'huit', 'neuf', 'dix', 'onze'];
    const denWords = {
        5: 'cinquièmes', 6: 'sixièmes', 10: 'dixièmes', 12: 'douzièmes',
        15: 'quinzièmes', 20: 'vingtièmes', 30: 'trentièmes',
    };
    if (!denWords[c] || !numWords[b]) return `${fracBrut(b, c)} d'heure`;
    const sg = c === 5 ? 'cinquième' : c === 6 ? 'sixième' : c === 10 ? 'dixième'
        : c === 12 ? 'douzième' : c === 15 ? 'quinzième' : c === 20 ? 'vingtième'
            : c === 30 ? 'trentième' : denWords[c];
    if (b === 1) return `un ${sg} d'heure`;
    return `${numWords[b]} ${denWords[c]} d'heure`;
};

const consigneHeureMinutes = (b, c) => {
    const verb = verbHeurePropre(b, c);
    const variations = [
        `<p>Combien de minutes y a-t-il dans <strong>${verb}</strong> ?</p>`,
        `<p>Convertis <strong>${verb}</strong> en minutes.</p>`,
        `<p>Exprime <strong>${verb}</strong> en minutes.</p>`,
    ];
    return randomChoice(variations);
};

const genHeureMinutesQcm = () => {
    const [b, c] = randomChoice(POOL_C);
    const bonne = (b * 60) / c;

    const cand = [
        b * 60,                                                // D1 : oubli c
        60 / c,                                                // D2 : oubli b (entier par contrainte c | 60)
        (b !== 0 && (c * 60) % b === 0) ? (c * 60) / b : null, // D3 : inversion
        60 - bonne,                                            // D4 : "heure moins"
        b + c,                                                 // D5 : aberration
        bonne + 1,                                             // D6 : off-by-one
    ].filter(v => v !== null && v !== bonne && Number.isInteger(v) && v >= 0);

    const distracteurs = cleanDistracteurs(bonne, cand, 4, (i) => {
        if (i === 0) return bonne + 5;
        if (i === 1) return Math.max(0, bonne - 5);
        if (i === 2) return bonne * 2;
        return 60 + bonne;
    });

    // Suffixe " min" pour lever l'ambiguïté d'unité dans les choix.
    const choix = [bonne, ...distracteurs].map(v => `${v} min`);
    const bonneStr = `${bonne} min`;
    const payload = makeQcm(choix, bonneStr, consigneHeureMinutes(b, c), opts(L_HMIN_QCM));
    payload._meta.generatorKey = 'fraction_heure_minutes';
    return payload;
};

const genHeureMinutesSaisie = () => {
    const [b, c] = randomChoice(POOL_C);
    const bonne = (b * 60) / c;
    const payload = makeSaisie(
        String(bonne),
        consigneHeureMinutes(b, c),
        { ...opts(L_HMIN_SAI), suffixe: ' minutes' },
    );
    payload._meta.generatorKey = 'fraction_heure_minutes';
    return payload;
};

// ═══════════════════════════════════════════════════════════
// EXERCICE 4 — fraction_heure_impropre_hm (Saisie uniquement)
// ═══════════════════════════════════════════════════════════

const consigneHeureImpropre = (b, c) => {
    // Écriture fractionnaire (arbitrage user 2026-08-02) : « quinze quarts
    // d'heure » n'est pas du français naturel — les fractions IMPROPRES
    // s'écrivent en chiffres ; les propres (POOL_C) gardent leurs mots
    // (« trois quarts d'heure », vrai usage oral).
    const frac = fracBrut(b, c);
    const variations = [
        `<p>Transforme <strong>${frac} d'heure</strong> en heures et minutes.</p>`,
        `<p>À combien d'heures et de minutes correspond <strong>${frac} d'heure</strong> ?</p>`,
        `<p>Exprime <strong>${frac} d'heure</strong> sous la forme « ... h ... min ».</p>`,
    ];
    return randomChoice(variations);
};

const genHeureImpropreSaisie = () => {
    const [b, c] = randomChoice(POOL_C2);
    const totalMin = (b * 60) / c;
    const h = Math.floor(totalMin / 60);
    const min = totalMin % 60;
    // Texte à trous avec 2 zones : heures et minutes.
    const phrase = `Résultat : {{${h}}} h {{${min}}} min`;
    const payload = makeTexteTrou(
        phrase,
        consigneHeureImpropre(b, c),
        { ...opts(L_HIMP_SAI), mode: 'saisie', strict: false },
    );
    payload._meta.generatorKey = 'fraction_heure_impropre_hm';
    return payload;
};

// ═══════════════════════════════════════════════════════════
// EXERCICE 5 — fraction_longueur_segment (QCM + Saisie + SVG)
// ═══════════════════════════════════════════════════════════

const consigneSegment = (L, b, c) => {
    const variations = [
        `<p>On a tracé un segment [AB] de longueur <strong>${L} mm</strong>.</p>{{element:0}}<p>On place un point C sur [AB] tel que AC = <strong>${fracBrut(b, c)}</strong> de [AB]. Quelle est la longueur AC, en millimètres ?</p>`,
        `<p>Un segment [AB] mesure <strong>${L} mm</strong>.</p>{{element:0}}<p>Un point C est placé sur [AB] tel que AC = <strong>${tex(`\\dfrac{${b}}{${c}} \\times \\text{AB}`)}</strong>. Calcule AC, en millimètres.</p>`,
        `<p>Voici un segment [AB] de <strong>${L} mm</strong>.</p>{{element:0}}<p>On cherche la longueur AC où C est un point de [AB] vérifiant AC = <strong>${fracBrut(b, c)}</strong> de AB. Donne la réponse en millimètres.</p>`,
    ];
    return randomChoice(variations);
};

const genSegmentQcm = () => {
    const [L, b, c] = randomChoice(POOL_D1);
    const bonne = (b * L) / c;
    const elAdd = svgImageElement(svgSegment(L));

    const cand = [
        b * L,                                                   // D1 : oubli c
        L / c,                                                   // D2 : oubli b (entier car c | L)
        (b !== 0 && (c * L) % b === 0) ? (c * L) / b : null,     // D3 : inversion (piège fort car peut > L)
        L - bonne,                                               // D4 : CB au lieu de AC (erreur géométrique)
        L - b,                                                   // D5 : aberration
        bonne + c,                                               // D6 : off-by-c
    ].filter(v => v !== null && v !== bonne && Number.isInteger(v) && v >= 0);

    const distracteurs = cleanDistracteurs(bonne, cand, 4, (i) => {
        if (i === 0) return bonne + 1;
        if (i === 1) return Math.max(1, bonne - 1);
        if (i === 2) return bonne * 2;
        return L + bonne;
    });

    const choix = [bonne, ...distracteurs].map(v => `${v} mm`);
    const bonneStr = `${bonne} mm`;
    const payload = makeQcm(choix, bonneStr, consigneSegment(L, b, c), { ...opts(L_SEG_QCM), elements_additionnels: elAdd });
    payload._meta.generatorKey = 'fraction_longueur_segment';
    return payload;
};

const genSegmentSaisie = () => {
    const [L, b, c] = randomChoice(POOL_D1);
    const bonne = (b * L) / c;
    const elAdd = svgImageElement(svgSegment(L));
    const payload = makeSaisie(
        String(bonne),
        consigneSegment(L, b, c),
        { ...opts(L_SEG_SAI), suffixe: ' mm', elements_additionnels: elAdd },
    );
    payload._meta.generatorKey = 'fraction_longueur_segment';
    return payload;
};

// ═══════════════════════════════════════════════════════════
// EXERCICE 6 — fraction_diagramme (Saisie + SVG)
// ═══════════════════════════════════════════════════════════

const genDiagrammeSaisie = () => {
    const scenario = randomChoice(POOL_D2);
    // On sélectionne une part lisible (résultat entier garanti par construction de POOL_D2).
    // On filtre les parts dont c | G.
    const partsValides = scenario.parts.filter(p => scenario.G % p.den === 0);
    if (partsValides.length === 0) return genDiagrammeSaisie(); // safety
    const part = randomChoice(partsValides);
    const resultat = (part.num * scenario.G) / part.den;
    const elAdd = svgImageElement(svgDiagramme(scenario));
    const fracTxt = fracBrut(part.num, part.den);

    const deLabel = dePrep(part.label); // "de " ou "d'"
    const variations = [
        `<p>${scenario.contexte}.</p>{{element:0}}<p>Combien ${dePrep(scenario.unite_pl)}${scenario.unite_pl} représente la catégorie <strong>${part.label}</strong> (${fracTxt}) ?</p>`,
        `<p>${scenario.contexte}.</p>{{element:0}}<p>D'après le diagramme, calcule la quantité ${deLabel}<strong>${part.label}</strong> en ${scenario.unite_pl}.</p>`,
        `<p>${scenario.contexte}.</p>{{element:0}}<p>La part <strong>${part.label}</strong> représente ${fracTxt} du total. Quelle est sa quantité en ${scenario.unite_pl} ?</p>`,
    ];
    const texte = randomChoice(variations);

    const payload = makeSaisie(
        String(resultat),
        texte,
        { ...opts(L_DIAG_SAI), suffixe: ' ' + scenario.unite_pl, elements_additionnels: elAdd },
    );
    payload._meta.generatorKey = 'fraction_diagramme';
    return payload;
};

// ═══════════════════════════════════════════════════════════
// EXERCICE 7 — egalite_expressions_frac (V/F)
// ═══════════════════════════════════════════════════════════

/**
 * On génère 6 lignes : 3 vraies + 3 fausses.
 * Chaque ligne compare deux expressions fractionnaires calculables mentalement.
 * Pool de lignes « vraies » et « fausses » pré-vérifiées.
 */
const LIGNES_VF = {
    vraies: [
        { ligne: 'Les trois quarts de 16 sont égaux à 6 × 48/24', lhs: 12, rhs: 12 },      // 3/4×16=12 ; 6×48/24=12
        { ligne: 'La moitié de 50 est égale à 5/4 × 20',           lhs: 25, rhs: 25 },      // 1/2×50=25 ; 5/4×20=25
        { ligne: 'Les deux cinquièmes de 20 sont égaux à 2 × 4',   lhs: 8,  rhs: 8  },      // 2/5×20=8 ; 2×4=8
        { ligne: 'Les cinq sixièmes de 12 sont égaux à 2/3 × 15',  lhs: 10, rhs: 10 },      // 5/6×12=10 ; 2/3×15=10
        { ligne: 'Un tiers de 30 est égal à 1/2 × 20',             lhs: 10, rhs: 10 },      // 1/3×30=10 ; 1/2×20=10
        { ligne: 'Les trois quarts de 20 sont égaux à 3/5 × 25',   lhs: 15, rhs: 15 },      // 3/4×20=15 ; 3/5×25=15
        { ligne: 'Les deux tiers de 18 sont égaux à 4/3 × 9',      lhs: 12, rhs: 12 },      // 2/3×18=12 ; 4/3×9=12
        { ligne: 'Un quart de 60 est égal à 1/2 × 30',             lhs: 15, rhs: 15 },      // 1/4×60=15 ; 1/2×30=15
        { ligne: 'Les deux tiers de 30 sont égaux à 4 × 5',        lhs: 20, rhs: 20 },      // 2/3×30=20 ; 4×5=20
    ],
    fausses: [
        { ligne: 'Les deux tiers de 30 sont égaux à 3/2 × 20',     lhs: 20, rhs: 30 },      // 20 ≠ 30
        { ligne: 'Un quart de 100 est égal à 1/2 × 40',            lhs: 25, rhs: 20 },      // 25 ≠ 20
        { ligne: 'Les deux cinquièmes de 20 sont égaux à 2/3 × 15', lhs: 8,  rhs: 10 },     // 8 ≠ 10
        { ligne: 'La moitié de 24 est égale à 3/4 × 20',           lhs: 12, rhs: 15 },      // 12 ≠ 15
        { ligne: 'Les trois quarts de 16 sont égaux à 2/5 × 30',   lhs: 12, rhs: 12 },      // ATTENTION: 12=12 → RETIRÉ
        { ligne: 'Un tiers de 27 est égal à 1/2 × 20',             lhs: 9,  rhs: 10 },      // 9 ≠ 10
        { ligne: 'Les trois cinquièmes de 25 sont égaux à 2/3 × 24', lhs: 15, rhs: 16 },    // 15 ≠ 16
        { ligne: 'Les cinq sixièmes de 18 sont égaux à 3/4 × 20',  lhs: 15, rhs: 15 },      // ATTENTION: 15=15 → RETIRÉ
        { ligne: 'Un quart de 40 est égal à 1/5 × 45',             lhs: 10, rhs: 9  },      // 10 ≠ 9
        { ligne: 'Les deux tiers de 21 sont égaux à 7/5 × 10',     lhs: 14, rhs: 14 },      // ATTENTION: 14=14 → RETIRÉ
        { ligne: 'Un cinquième de 35 est égal à 1/4 × 24',         lhs: 7,  rhs: 6  },      // 7 ≠ 6
    ],
};

/** Filtrage final : on garde uniquement les vraies où lhs === rhs et les fausses où lhs !== rhs. */
const LIGNES_VF_FILTERED = {
    vraies: LIGNES_VF.vraies.filter(l => l.lhs === l.rhs),
    fausses: LIGNES_VF.fausses.filter(l => l.lhs !== l.rhs),
};

const genEgaliteVF = () => {
    // 2 vraies + 2 fausses : 6 lignes c'était trop (arbitrage user 2026-08-02)
    const vrais = shuffle(LIGNES_VF_FILTERED.vraies).slice(0, 2);
    const faux = shuffle(LIGNES_VF_FILTERED.fausses).slice(0, 2);
    if (vrais.length < 2 || faux.length < 2) {
        throw new Error('egalite_expressions_frac: pool insuffisant');
    }
    // Les lignes du pool sont écrites « 2/3 × 15 » ; à l'affichage la fraction
    // passe en KaTeX (audit fractions en texte, 2026-09-06).
    const indexed = [
        ...vrais.map(l => ({ l: ligneVfTex(l.ligne), br: 0 })),
        ...faux.map(l => ({ l: ligneVfTex(l.ligne), br: 1 })),
    ];
    const shuf = shuffle(indexed);
    const payload = makeVraiFaux(
        shuf.map(x => x.l),
        shuf.map(x => x.br),
        `<p>Pour chaque ligne, dis si l'égalité est vraie ou fausse.</p>`,
        { ...opts(L_VF), colonnes: ['Vrai', 'Faux'] },
    );
    payload._meta.generatorKey = 'egalite_expressions_frac';
    return payload;
};

// ═══════════════════════════════════════════════════════════
// EXERCICE 8 — multiplication_schema_bandes (QCM + Saisie + SVG)
// ═══════════════════════════════════════════════════════════

const consigneBandes = (n, b, c, exigerIrreductible = false) => {
    const suffixe = exigerIrreductible
        ? ` Donne ton résultat sous la forme d'une fraction irréductible.`
        : '';
    const variations = [
        `<p>Calcule <strong>${tex(`${n} \\times \\dfrac{${b}}{${c}}`)}</strong> en t'aidant du schéma ci-dessous.${suffixe}</p>{{element:0}}`,
        `<p>D'après le schéma ci-dessous, que vaut <strong>${tex(`${n} \\times \\dfrac{${b}}{${c}}`)}</strong> ?${suffixe}</p>{{element:0}}`,
        `<p>Le schéma ci-dessous représente la multiplication <strong>${tex(`${n} \\times \\dfrac{${b}}{${c}}`)}</strong>. Donne son résultat.${suffixe}</p>{{element:0}}`,
    ];
    return randomChoice(variations);
};

/** Valeur numérique d'une chaîne "a/b" ou "a" pour détecter les doublons. */
const parseFracOrInt = (s) => {
    if (typeof s !== 'string') return NaN;
    const m = s.match(/^(-?\d+)\/(\d+)$/);
    if (m) return Number(m[1]) / Number(m[2]);
    const m2 = s.match(/^(-?\d+)$/);
    if (m2) return Number(m2[1]);
    return NaN;
};

const genBandesQcm = () => {
    const [n, b, c] = randomChoice(POOL_H);
    const elAdd = svgImageElement(svgBandes(n, b, c), 'small');
    const numBrut = n * b;
    const { num, den } = reduceFrac(numBrut, c);
    const estEntier = den === 1;
    const bonne = estEntier ? String(num) : `${num}/${den}`;
    const bonneVal = num / den;

    // Distracteurs selon fiche §4 — on n'émet QUE ceux qui sont numériquement distincts de la bonne réponse.
    const cand = [];
    const tryPush = (s) => {
        const v = parseFracOrInt(s);
        if (Number.isFinite(v) && Math.abs(v - bonneVal) > 1e-9) cand.push(s);
    };
    tryPush(String(n * b));              // D1 : oubli dénominateur
    tryPush(`${n * c}/${b}`);            // D2 : inversion b↔c
    tryPush(`${n + b}/${c}`);            // D3 : addition au num
    if (!estEntier) tryPush(`${numBrut}/${c}`);  // D4 : non simplifié
    tryPush(String(Math.floor(numBrut / c)));    // D5 : tronqué à l'entier
    tryPush(`${n}/${c}`);                // D6 : oubli de b
    tryPush(`${b}/${c}`);                // D7 : oubli de n (on ne considère qu'une bande)

    const distracteurs = cleanDistracteurs(bonne, cand, 4, (i) => {
        const fbk = estEntier
            ? [String(num + 1), String(Math.max(0, num - 1)), String(num + 2), String(n + c)]
            : [`${num + 1}/${den}`, `${num}/${den + 1}`, `${num + den}/${den}`, `${num}/${den + 2}`];
        return fbk[i % fbk.length];
    });

    const toKatex = (s) => {
        const m = s.match(/^(-?\d+)\/(\d+)$/);
        return m ? fmtItem(Number(m[1]), Number(m[2])) : s;
    };
    const bonneDisplay = toKatex(bonne);
    // Dédup APRÈS mapping KaTeX (certains distracteurs se réduisent à la même forme)
    const rawChoix = [bonne, ...distracteurs].map(toKatex);
    const seen = new Set();
    const choix = rawChoix.filter(c => !seen.has(c) && seen.add(c));
    // Compléter si dédup a laissé moins de 4 choix (garantie min 3)
    let bump = 1;
    while (choix.length < 4 && bump < 20) {
        const filler = estEntier
            ? fmtItem(num + bump, 1)
            : fmtItem(num + bump, den);
        if (!seen.has(filler) && filler !== bonneDisplay) {
            seen.add(filler);
            choix.push(filler);
        }
        bump++;
    }
    const payload = makeQcm(choix, bonneDisplay, consigneBandes(n, b, c), { ...opts(L_BANDES_QCM), elements_additionnels: elAdd });
    payload._meta.generatorKey = 'multiplication_schema_bandes';
    return payload;
};

const genBandesSaisie = () => {
    const [n, b, c] = randomChoice(POOL_H);
    const elAdd = svgImageElement(svgBandes(n, b, c), 'small');
    const { num, den } = reduceFrac(n * b, c);
    // Si le résultat n'est pas entier, on exige explicitement la forme irréductible
    // pour que l'élève qui tape « 6/4 » au lieu de « 3/2 » ait la consigne claire.
    const texte = consigneBandes(n, b, c, den !== 1);

    let payload;
    if (den === 1) {
        // Résultat entier : Saisie simple.
        payload = makeSaisie(String(num), texte, { ...opts(L_BANDES_SAI), elements_additionnels: elAdd });
    } else {
        // Résultat fractionnaire : texte_trou avec 2 zones num/den.
        // Étais \rule (revue 2026-08-02, même fix que 620/621/623/624) : sans
    // eux la barre de fraction disparaît sous les champs de saisie
    const phrase = `$\\text{Résultat : } \\dfrac{{{${num}}}\\rule[-0.55em]{0pt}{0.55em}}{\\rule{0pt}{1.3em}{{${den}}}}$`;
        payload = makeTexteTrou(
            phrase,
            texte,
            { ...opts(L_BANDES_SAI), mode: 'saisie', strict: false, elements_additionnels: elAdd },
        );
    }
    payload._meta.generatorKey = 'multiplication_schema_bandes';
    return payload;
};

// ═══════════════════════════════════════════════════════════
// REGISTRE
// ═══════════════════════════════════════════════════════════

const GENERATORS = {
    fraction_entier_direct_qcm_unique:            genFractionEntierDirectQcm,
    fraction_entier_direct_texte:                 genFractionEntierDirectSaisie,
    fraction_entier_fraction_resultat_qcm_unique: genFractionResultatQcm,
    fraction_entier_fraction_resultat_texte:      genFractionResultatSaisie,
    fraction_heure_minutes_qcm_unique:            genHeureMinutesQcm,
    fraction_heure_minutes_texte:                 genHeureMinutesSaisie,
    fraction_heure_impropre_hm_texte:             genHeureImpropreSaisie,
    fraction_longueur_segment_qcm_unique:         genSegmentQcm,
    fraction_longueur_segment_texte:              genSegmentSaisie,
    fraction_diagramme_texte:                     genDiagrammeSaisie,
    egalite_expressions_frac_tableau_vf:          genEgaliteVF,
    multiplication_schema_bandes_qcm_unique:      genBandesQcm,
    multiplication_schema_bandes_texte:           genBandesSaisie,
};

const FORMAT_LABELS = {
    qcm_unique: 'QCM', qcm_multiple: 'QCM multiple', texte: 'Saisie',
    relier: 'Relier', tableau_vf: 'Vrai/Faux',
};

const buildFormats = (prefix) =>
    Object.keys(GENERATORS).filter((k) => k.startsWith(`${prefix}_`))
        .map((k) => ({ key: k, label: FORMAT_LABELS[k.slice(prefix.length + 1)] || k }));

export const CATEGORIES = [
    { id: 'fraction_entier_direct',            label: L_ENT_QCM,    formatsList: buildFormats('fraction_entier_direct') },
    { id: 'fraction_entier_fraction_resultat', label: L_FRAC_QCM,   formatsList: buildFormats('fraction_entier_fraction_resultat') },
    { id: 'fraction_heure_minutes',            label: L_HMIN_QCM,   formatsList: buildFormats('fraction_heure_minutes') },
    { id: 'fraction_heure_impropre_hm',        label: L_HIMP_SAI,   formatsList: buildFormats('fraction_heure_impropre_hm') },
    { id: 'fraction_longueur_segment',         label: L_SEG_QCM,    formatsList: buildFormats('fraction_longueur_segment') },
    { id: 'fraction_diagramme',                label: L_DIAG_SAI,   formatsList: buildFormats('fraction_diagramme') },
    { id: 'egalite_expressions_frac',          label: L_VF,         formatsList: buildFormats('egalite_expressions_frac') },
    { id: 'multiplication_schema_bandes',      label: L_BANDES_QCM, formatsList: buildFormats('multiplication_schema_bandes') },
];

// Exports utiles au stress test
export const _internals = {
    gcd, reduceFrac, fmtFrac, fmtKatex, fmtItem, isValid626,
    POOL_A, POOL_B, POOL_B_IRRED, POOL_C, POOL_C2, POOL_D1, POOL_D2, POOL_H,
    LIGNES_VF_FILTERED,
    svgSegment, svgBandes, svgDiagramme,
    GENERATORS,
};

// ─── Point d'entrée ───
export const generate = (typeExercice) => {
    let key;
    if (typeExercice && GENERATORS[typeExercice]) {
        key = typeExercice;
    } else if (typeExercice) {
        const matching = Object.keys(GENERATORS).filter((k) =>
            k.startsWith(typeExercice) || k.includes(typeExercice));
        key = matching.length ? randomChoice(matching) : randomChoice(Object.keys(GENERATORS));
    } else {
        key = randomChoice(Object.keys(GENERATORS));
    }
    const payload = GENERATORS[key]();
    // Pas de calculatrice (arbitrage user 2026-08-02, check exhaustif des
    // pools : tout se fait de tête ou au brouillon via « divise d'abord »).
    // EXCEPTION (arbitrage user) : le V/F cumule jusqu'à 8 calculs — la
    // calculette accélère sans toucher au jugement d'égalité (même
    // mécanique que le moteur « chaîne » de 652).
    payload.contenu = { ...payload.contenu, calculatrice: key === 'egalite_expressions_frac_tableau_vf' };
    // Toujours forcer la clé complète (les générateurs ne posent que le préfixe
    // catégorie ce qui cassait le "regenerate dans le même format")
    payload._meta.generatorKey = key;
    return payload;
};
